import { useAuthStore } from "store/authStore";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface Tokens {
  accessToken: string;
  refreshToken: string;
}

interface ApiSuccessResponse<T> {
  success: true;
  message: string;
  data: T;
}

/** Decode JWT payload (no verification — read claims from access or refresh token) */
function decodeJwtPayload(token: string): {
  sub?: string;
  exp?: number;
  purpose?: string;
} {
  try {
    const base64 = token.split(".")[1];
    if (!base64) return {};
    const json = atob(base64.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return {};
  }
}

/** Refresh access this many seconds before `exp` to avoid edge-of-expiry 401s */
const ACCESS_EXPIRY_SKEW_SEC = 60;

function getJwtExpMs(token: string): number | null {
  const { exp } = decodeJwtPayload(token);
  if (exp == null || typeof exp !== "number") return null;
  return exp * 1000;
}

/** True if token has `exp` and it is past (with optional skew for access token). */
function isJwtExpired(token: string, skewSec: number): boolean {
  const expMs = getJwtExpMs(token);
  if (expMs === null) return false;
  return Date.now() >= expMs - skewSec * 1000;
}

let refreshPromise: Promise<boolean> | null = null;

/**
 * Attempts to refresh tokens. Returns true if successful, false otherwise.
 * Uses a lock to prevent concurrent refresh requests.
 */
async function attemptRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  const state = useAuthStore.getState();
  const refreshToken = state.refreshToken;

  if (!refreshToken) {
    useAuthStore.getState().clearAuth();
    return false;
  }

  refreshPromise = (async () => {
    try {
      const tokens = await refreshTokens({ refreshToken });
      useAuthStore.getState().setTokens(
        tokens.accessToken,
        tokens.refreshToken ?? refreshToken
      );
      return true;
    } catch {
      useAuthStore.getState().clearAuth();
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Before an authenticated request: if refresh JWT is expired, clear session.
 * If access is missing/expired (within skew) and refresh is still valid, rotate tokens.
 */
async function ensureAccessTokenFresh(): Promise<void> {
  const { accessToken, refreshToken } = useAuthStore.getState();

  if (refreshToken && isJwtExpired(refreshToken, 0)) {
    useAuthStore.getState().clearAuth();
    throw new Error("Session expired. Please sign in again.");
  }

  const accessStale =
    !accessToken || isJwtExpired(accessToken, ACCESS_EXPIRY_SKEW_SEC);

  if (!accessStale) return;

  if (!refreshToken) {
    if (accessToken) useAuthStore.getState().clearAuth();
    throw new Error("Session expired. Please sign in again.");
  }

  const ok = await attemptRefresh();
  if (!ok) {
    throw new Error("Session expired. Please sign in again.");
  }
}

/**
 * Exchange refresh token for new access and refresh tokens.
 * Call this when access token is expired. If refresh token is also expired, throws.
 */
export async function refreshTokens(params: {
  refreshToken: string;
}): Promise<Tokens> {
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });

  const json = await res.json();

  if (!res.ok) {
    const message = json?.message || json?.error || "Session expired";
    throw new Error(message);
  }

  const data = (json as ApiSuccessResponse<Tokens>).data;
  if (!data?.accessToken) {
    throw new Error("Invalid response from server");
  }

  return data;
}

export type ApiFetchOptions = RequestInit & {
  /**
   * When true, a 401 response is returned as-is (no refresh + retry).
   * Use for endpoints where 401 means invalid user input (e.g. wrong current password),
   * not an expired access token.
   */
  skip401Retry?: boolean;
};

/**
 * Authenticated fetch: before the request, refreshes tokens if access is expired
 * (or near expiry) while refresh is still valid; logs out if refresh is expired.
 * On 401, retries once after refresh (clock skew / revocation edge cases), unless
 * `skip401Retry` is set.
 * Use this for all API calls that require authentication.
 */
export async function apiFetch(
  url: string,
  options: ApiFetchOptions = {}
): Promise<Response> {
  const { skip401Retry, ...fetchInit } = options;

  await ensureAccessTokenFresh();

  const getAuthHeaders = (): Record<string, string> => {
    const token = useAuthStore.getState().accessToken;
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  };

  const doFetch = (): Promise<Response> => {
    const headers = new Headers(fetchInit.headers);
    Object.entries(getAuthHeaders()).forEach(([k, v]) => headers.set(k, v));
    return fetch(url, { ...fetchInit, headers });
  };

  let res = await doFetch();

  if (res.status === 401 && !skip401Retry) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      res = await doFetch();
    }
    if (res.status === 401) {
      throw new Error("Session expired. Please sign in again.");
    }
  }

  return res;
}

export async function login(credentials: LoginCredentials): Promise<Tokens> {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(credentials),
  });

  const json = await res.json();

  if (!res.ok) {
    const message = json?.message || json?.error || "Invalid email or password";
    throw new Error(message);
  }

  const data = (json as ApiSuccessResponse<Tokens>).data;
  if (!data?.accessToken) {
    throw new Error("Invalid response from server");
  }

  return data;
}

/**
 * Logs out the current user. Invalidates the refresh token on the server.
 * Caller should clear local auth state and redirect regardless of success.
 */
export async function logout(accessToken: string): Promise<void> {
  const res = await fetch(`${API_BASE}/auth/logout`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!res.ok) {
    const json = await res.json();
    const message = json?.message || json?.error || "Logout failed";
    throw new Error(message);
  }
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface SetupPasswordPayload {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

function formatApiErrorMessage(json: unknown): string {
  if (!json || typeof json !== "object") return "Request failed";
  const rec = json as { message?: unknown };
  const msg = rec.message;
  if (Array.isArray(msg)) {
    return msg.map(String).filter(Boolean).join(" ");
  }
  if (typeof msg === "string" && msg.trim()) return msg;
  const err = (json as { error?: unknown }).error;
  if (typeof err === "string" && err.trim()) return err;
  return "Unable to change password";
}

/**
 * Super-admin only. On success the server revokes all sessions; caller should clear auth and redirect to sign-in.
 * Uses `skip401Retry` so a wrong current password (401) is not mistaken for an expired access token.
 */
export async function changePassword(
  payload: ChangePasswordPayload
): Promise<string> {
  const body = {
    currentPassword: payload.currentPassword.trim(),
    newPassword: payload.newPassword.trim(),
    confirmPassword: payload.confirmPassword.trim(),
  };

  const res = await apiFetch(`${API_BASE}/auth/change-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    skip401Retry: true,
  });

  let json: unknown = {};
  try {
    json = await res.json();
  } catch {
    /* empty */
  }

  if (!res.ok) {
    throw new Error(formatApiErrorMessage(json));
  }

  const message =
    typeof (json as ApiSuccessResponse<unknown>)?.message === "string"
      ? (json as ApiSuccessResponse<unknown>).message
      : "Password changed successfully.";

  return message;
}

export interface ResetPasswordPayload {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Requests a password reset link for the given email. The backend always responds 200
 * (to prevent user enumeration), so a resolved promise only means the request was
 * accepted — it does not confirm the email belongs to a real account.
 */
export async function forgotPassword(email: string): Promise<string> {
  const res = await fetch(`${API_BASE}/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: email.trim() }),
  });

  let json: unknown = {};
  try {
    json = await res.json();
  } catch {
    /* empty */
  }

  if (!res.ok) {
    throw new Error(formatApiErrorMessage(json));
  }

  const message =
    typeof (json as ApiSuccessResponse<unknown>)?.message === "string"
      ? (json as ApiSuccessResponse<unknown>).message
      : "If that email is registered, a reset link has been sent.";

  return message;
}

/**
 * Completes a forgot-password flow using the token from the reset email.
 * Distinct from `setupPassword` (account activation): the backend validates the token's
 * `purpose` claim (PASSWORD_RESET vs PASSWORD_SETUP), so the endpoints are not interchangeable.
 */
export async function resetPassword(
  payload: ResetPasswordPayload
): Promise<string> {
  const body = {
    token: payload.token.trim(),
    newPassword: payload.newPassword.trim(),
    confirmPassword: payload.confirmPassword.trim(),
  };

  const res = await fetch(`${API_BASE}/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  let json: unknown = {};
  try {
    json = await res.json();
  } catch {
    /* empty */
  }

  if (!res.ok) {
    throw new Error(formatApiErrorMessage(json));
  }

  const message =
    typeof (json as ApiSuccessResponse<unknown>)?.message === "string"
      ? (json as ApiSuccessResponse<unknown>).message
      : "Password reset successfully.";

  return message;
}

/**
 * Reads the `purpose` claim from a setup/reset JWT (no verification — the server re-validates).
 * The shared `/auth/reset-password` page uses this to call the correct endpoint:
 * PASSWORD_RESET → resetPassword, otherwise → setupPassword.
 */
export function getTokenPurpose(token: string): string | undefined {
  return decodeJwtPayload(token).purpose;
}

export async function setupPassword(
  payload: SetupPasswordPayload
): Promise<string> {
  const body = {
    token: payload.token.trim(),
    newPassword: payload.newPassword.trim(),
    confirmPassword: payload.confirmPassword.trim(),
  };

  const res = await fetch(`${API_BASE}/auth/setup-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  let json: unknown = {};
  try {
    json = await res.json();
  } catch {
    /* empty */
  }

  if (!res.ok) {
    throw new Error(formatApiErrorMessage(json));
  }

  const message =
    typeof (json as ApiSuccessResponse<unknown>)?.message === "string"
      ? (json as ApiSuccessResponse<unknown>).message
      : "Password set successfully.";

  return message;
}
